import React from "react";
import CareersPage from "@/pages/public/CareersPage";
import { useParams as useCareersParams } from "react-router-dom";
const CareersAdminRedirect = () => { const { aid } = useCareersParams(); return <Navigate to={`/app/myschoolrx/careers/applicants?id=${aid}`} replace />; };
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Contained per-tab impersonation: when THIS tab holds an `impersonation_token`
// in sessionStorage (set only by a Demo/Test launch that opens a NEW tab), attach
// it as Authorization: Bearer + x-session-token so the tab authenticates as the
// target user. It is per-tab (sessionStorage), cleared on any login, and always
// accompanied by a visible banner — so it can't silently leak into a real session.
// The server only trusts a real sign-in. School / client-portal screens that send
// x-tenant-id get the matching stored school session token attached automatically.
const schoolTokenFor = (tenantId) => {
  if (!tenantId) return null;
  try {
    const d = JSON.parse(localStorage.getItem("diag_session") || "null");
    if (d?.session_token && d?.tenant?.id === tenantId) return d.session_token;
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (!k || !k.startsWith("portal_session_")) continue;
      const p = JSON.parse(sessionStorage.getItem(k) || "null");
      if (p?.session_token && p?.tenant?.id === tenantId) return p.session_token;
    }
  } catch {}
  return null;
};
import { installContextShims, ctxToken, ctxId, pendingWrites, reportContextError } from "@/lib/roleContext";
import { enforceBrowserSession } from "@/lib/sessionPersistence";

enforceBrowserSession();
installContextShims();
const isWrite = (m) => m && !["GET", "HEAD", "OPTIONS"].includes(String(m).toUpperCase());
axios.interceptors.request.use((config) => {
  try {
    const h0 = config.headers || {};
    const tid0 = h0.get ? h0.get("x-tenant-id") : h0["x-tenant-id"];
    // In a switched context, a school-scoped token for the requested school (e.g. the portfolio's own
    // assessment session) wins over the context's base token.
    const t = ctxToken() ? ((h0.get ? h0.get("x-session-token") : h0["x-session-token"]) || schoolTokenFor(tid0) || ctxToken())
      : sessionStorage.getItem("impersonation_token");
    if (t) config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}`, "x-session-token": t };
    else {
      const h = config.headers || {};
      const tid = (h.get ? h.get("x-tenant-id") : h["x-tenant-id"]);
      const st = !(h.get ? h.get("x-session-token") : h["x-session-token"]) && schoolTokenFor(tid);
      if (st) config.headers = Object.assign(h, { "x-session-token": st });
    }
    if (ctxId()) config.headers = { ...(config.headers || {}), "x-context-id": ctxId() };
    if (isWrite(config.method)) { pendingWrites.n += 1; config.__msxWrite = true; }
    if ((config.url || "").includes("/logout")) ["ctx_token", "ctx_id", "ctx_info", "ctx_diag_session"].forEach((k) => sessionStorage.removeItem(k));
  } catch {}
  return config;
});
axios.interceptors.response.use((res) => {
  if (res.config?.__msxWrite) pendingWrites.n = Math.max(0, pendingWrites.n - 1);
  return res;
}, (err) => {
  if (err?.config?.__msxWrite) pendingWrites.n = Math.max(0, pendingWrites.n - 1);
  reportContextError(err?.response?.status, err?.response?.data);
  return Promise.reject(err);
});
const _origFetch = window.fetch.bind(window);
window.fetch = (input, init = {}) => {
  let write = false;
  try {
    const t = ctxToken() || sessionStorage.getItem("impersonation_token");
    const url = typeof input === "string" ? input : (input && input.url) || "";
    if (url.includes("/logout")) ["ctx_token", "ctx_id", "ctx_info", "ctx_diag_session"].forEach((k) => sessionStorage.removeItem(k));
    if (url.includes("/api/")) {
      const headers = new Headers((init && init.headers) || {});
      const scoped = ctxToken() && !headers.has("x-session-token") && schoolTokenFor(headers.get("x-tenant-id"));
      if (scoped) {
        headers.set("Authorization", `Bearer ${scoped}`);
        headers.set("x-session-token", scoped);
      } else if (t) {
        if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${t}`);
        if (!headers.has("x-session-token")) headers.set("x-session-token", t);
      } else if (!headers.has("x-session-token")) {
        const st = schoolTokenFor(headers.get("x-tenant-id"));
        if (st) headers.set("x-session-token", st);
      }
      if (ctxId()) headers.set("x-context-id", ctxId());
      init = { ...init, headers };
      write = isWrite(init.method) && !url.includes("/api/context/");
    }
  } catch {}
  if (write) pendingWrites.n += 1;
  return _origFetch(input, init).then((res) => {
    if ([401, 403, 409, 423].includes(res.status)) res.clone().json().then((b) => reportContextError(res.status, b)).catch(() => {});
    return res;
  }).finally(() => { if (write) pendingWrites.n = Math.max(0, pendingWrites.n - 1); });
};

import {
  FAQContent,
  HowItWorksContent,
  IndustriesContent,
  PricingContent,
  ServicesContent,
} from "@/pages/PublicSite";
import MySchoolRxAbout from "@/pages/public/MySchoolRxAbout";
import MySchoolRxContact from "@/pages/public/MySchoolRxContact";
import MySchoolRxPrivacy from "@/pages/public/MySchoolRxPrivacy";
import MySchoolRxTerms from "@/pages/public/MySchoolRxTerms";
import MySchoolRxSchools from "@/pages/public/MySchoolRxSchools";
import MySchoolRxPortfolios from "@/pages/public/MySchoolRxPortfolios";
import HomePage from "@/pages/public/HomePage";
import GlobalMessageBar from "@/components/GlobalMessageBar";

import IndustryPage from "@/pages/public/IndustryPage";
import CEDIndustryPage from "@/pages/public/CEDIndustryPage";
import FAQArticlePage from "@/pages/public/FAQArticlePage";

import AppArea from "@/pages/AppArea";
import PortalArea from "@/pages/PortalArea";
import NJEAPortal from "@/pages/NJEAPortal";
import CaporilliLanding from "@/pages/public/CaporilliLanding";
import ScenarioTourPage from "@/pages/public/ScenarioTourPage";
import ExpertsPage from "@/pages/public/ExpertsPage";
import SignupRouter from "@/pages/public/SignupRouter";
import { PilotEntry, PilotSingleSchool, PilotPortfolio } from "@/pages/public/PilotOnboarding";
import SchoolSignupWizard from "@/pages/public/SchoolSignupWizard";
import UserGuidePage from "@/pages/public/UserGuidePage";
import SchoolCheckoutPage from "@/pages/public/SchoolCheckoutPage";
import CheckoutSuccess from "@/pages/public/CheckoutSuccess";
import APPurchaseOrderPage from "@/pages/public/APPurchaseOrderPage";
import SchoolWorkspaceSetup from "@/pages/public/SchoolWorkspaceSetup";
import PortfolioSignup from "@/pages/public/PortfolioSignup";
import SampleBoardReport from "@/pages/public/SampleBoardReport";
import DiagnosticLogin from "@/pages/diagnostic/DiagnosticLogin";
import ResetPassword from "@/pages/diagnostic/ResetPassword";
import { DiagnosticDashboard, DiagnosticForm, DiagnosticOnboarding, DiagnosticCompletion } from "@/pages/diagnostic/CaporilliWorkspace";
import TenantSettingsPage from "@/pages/diagnostic/TenantSettingsPage";
import TenantUsersPage from "@/pages/diagnostic/TenantUsersPage";
import AIPlanUpgradePage from "@/pages/diagnostic/AIPlanUpgradePage";
import PortfolioOwnerArea from "@/pages/portfolio/PortfolioOwnerArea";
import InviteAcceptPage from "@/pages/portfolio/InviteAcceptPage";
import SalesPortalArea from "@/pages/sales/SalesPortalArea";
import EmployeeSetup from "@/pages/public/EmployeeSetup";
import LeadLink from "@/pages/public/LeadLink";
import ChangePasswordScreen from "@/pages/app/ChangePasswordScreen";
import { Toaster } from "@/components/ui/sonner";
import { useDemoMode } from "@/hooks/useDemoMode";
import { useLocation, useNavigate, Navigate as RRNavigate } from "react-router-dom";
import { ActAsBanner } from "@/components/ActAsBanner";
import { RoleContextController } from "@/components/RoleContextController";

// Backward-compatible redirect: /caporilli-diagnostic[/...]?query#hash ->
// /myschoolrx[/...] preserving subpath, query params and hash.
const LegacyDiagnosticRedirect = () => {
  const location = useLocation();
  const target =
    location.pathname.replace(/^\/(caporilli-diagnostic|school-quality-diagnostic)/, "/myschoolrx") +
    location.search +
    location.hash;
  return <RRNavigate to={target} replace />;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Scroll public marketing routes to the top on navigation (no hash anchor).
// Scoped to public paths so Diagnostic resume / portal scroll behavior is untouched.
const ScrollToTop = () => {
  const location = useLocation();
  React.useEffect(() => {
    const p = location.pathname || "";
    const isPublic = p === "/" || p.startsWith("/myschoolrx") ||
      p.startsWith("/privacy") || p.startsWith("/terms") || p.startsWith("/about") || p.startsWith("/contact");
    if (isPublic && !location.hash) window.scrollTo(0, 0);
    if (!isPublic || !location.hash) return undefined;
    // Deep link to a landing section (e.g. #faqs): wait for it to render, then scroll.
    let tries = 0;
    const t = setInterval(() => {
      const el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (el) el.scrollIntoView();
      if (el || ++tries > 30) clearInterval(t);
    }, 100);
    return () => clearInterval(t);
  }, [location.pathname, location.hash]);
  return null;
};

// Portfolio users never use a school's editable workspace: an Open-School session landing
// on /diagnostic goes to the view-only page; their own assessment stays on /diagnostic/form.
const PortfolioSessionGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    const p = location.pathname || "";
    if (!p.startsWith("/diagnostic") || p.startsWith("/diagnostic/login") || p.startsWith("/diagnostic/signup")) return;
    let s = null;
    try { s = JSON.parse(localStorage.getItem("diag_session") || "null"); } catch { return; }
    const tid = s?.tenant?.id;
    if (s?.acting_as !== "portfolio" || !tid) return;
    const own = (s.portfolio_context || {}).perspective === "portfolio";
    if (!own) navigate(`/portfolio/schools/${tid}/diagnostic`, { replace: true });
    else if (!p.startsWith("/diagnostic/form")) navigate(`/portfolio/schools/${tid}`, { replace: true });
  }, [location.pathname, navigate]);
  return null;
};

// myschoolrx.com must never show 589 Labs consulting pages; other hosts render them as before.
const IS_MSX_HOST = typeof window !== "undefined" && ["myschoolrx.com", "www.myschoolrx.com"].includes(window.location.hostname);
const msxOr = (to, element) => (IS_MSX_HOST ? <Navigate to={to} replace /> : element);

// Test Lab impersonation: stores the per-tab Bearer token, then hard-navigates
// into the target workspace so all subsequent requests authenticate as the test user.
const DemoLaunchHandler = () => {
  React.useEffect(() => {
    try {
      const raw = window.location.hash.replace(/^#/, "");
      const payload = JSON.parse(decodeURIComponent(escape(atob(raw))));
      const { token, redirect, realm, email, name, env } = payload;
      sessionStorage.setItem("impersonation_token", token);
      sessionStorage.setItem("impersonation_email", email || name || "demo");
      sessionStorage.setItem("impersonation_mode", "demo");
      sessionStorage.setItem("impersonation_env", env || "");
      if (realm === "tenant" && payload.diag_session) {
        localStorage.setItem("diag_session", JSON.stringify(payload.diag_session));
      }
      window.location.replace(redirect || "/diagnostic");
    } catch {
      window.location.replace("/app/demo");
    }
  }, []);
  return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", fontFamily: "sans-serif", color: "#64748b" }}>Opening demo environment…</div>;
};

const ImpersonateHandler = () => {
  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const token = p.get("token");
    const redirect = p.get("redirect") || "/diagnostic";
    const email = p.get("email") || "";
    const mode = p.get("mode") || (token ? "session" : "diag");
    try {
      if (mode === "session" && token) sessionStorage.setItem("impersonation_token", token);
      sessionStorage.setItem("impersonation_email", email);
      sessionStorage.setItem("impersonation_mode", mode);
    } catch {}
    window.location.replace(redirect);
  }, []);
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", fontFamily: "sans-serif", color: "#64748b" }}>
      Opening test session…
    </div>
  );
};

const ImpersonationBanner = () => {
  const [active, setActive] = React.useState(() => {
    try { return !!sessionStorage.getItem("impersonation_email"); } catch { return false; }
  });
  const email = (() => { try { return sessionStorage.getItem("impersonation_email") || ""; } catch { return ""; } })();
  const isDemo = (() => { try { return sessionStorage.getItem("impersonation_mode") === "demo"; } catch { return false; } })();
  // Portfolio & Sales portals render their own "Return to Admin" control in the
  // top bar, so the fixed banner (which overlaps the message bar) is suppressed there.
  const path = (typeof window !== "undefined" && window.location.pathname) || "";
  const handledByPortal = path.startsWith("/portfolio") || path.startsWith("/sales") || path.startsWith("/diagnostic");
  if (!active || handledByPortal) return null;
  const exit = () => {
    try {
      const mode = sessionStorage.getItem("impersonation_mode");
      const token = sessionStorage.getItem("impersonation_token");
      if (mode === "session" && token) axios.post(`${API_BASE}/api/sales/test-lab/impersonate/exit`, { token }).catch(() => {});
      localStorage.removeItem("diag_session");
      sessionStorage.removeItem("impersonation_token");
      sessionStorage.removeItem("impersonation_email");
      sessionStorage.removeItem("impersonation_mode");
      sessionStorage.removeItem("impersonation_env");
    } catch {}
    setActive(false);
    if (window.opener) window.close();
    else window.location.replace("/app/demo");
  };
  return (
    <div data-testid="impersonation-banner"
      className="fixed top-0 inset-x-0 z-[10000] flex items-center justify-center gap-3 bg-amber-500 text-black text-xs font-bold px-4 py-2 shadow-lg">
      <span className="uppercase tracking-wide">{isDemo ? "⭑ Demo Environment" : "⚠ Test Impersonation"}</span>
      <span className="font-medium normal-case">Viewing as {email || "demo account"} — {isDemo ? "isolated demo data." : "this is TEST DATA only."}</span>
      <button onClick={exit} data-testid="impersonation-exit-btn"
        className="ml-2 rounded bg-black/80 text-white px-3 py-1 hover:bg-black">{isDemo ? "Return to Admin" : "Exit"}</button>
    </div>
  );
};

const DemoModeBadge = () => {  const isDemo = useDemoMode();
  if (!isDemo) return null;
  return (
    <div
      data-testid="demo-mode-pill"
      className="fixed bottom-4 left-4 z-[9999] flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-400 backdrop-blur-xl shadow-lg cursor-default select-none"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" />
      </span>
      Demo Mode
    </div>
  );
};

function App() {
  return (
    <div>
      <BrowserRouter>
        <a href="#main" className="skip-link" data-testid="skip-to-main">Skip to main content</a>
        <ScrollToTop />
        <PortfolioSessionGuard />
        <ActAsBanner />
        <RoleContextController />
        <DemoModeBadge />
        <ImpersonationBanner />
        <GlobalMessageBar />
        <Routes>
          {/* Public site */}
          <Route path="/" element={<Navigate to="/myschoolrx" replace />} />
          <Route path="/services" element={msxOr("/myschoolrx", <ServicesContent />)} />
          <Route path="/how-it-works" element={msxOr("/myschoolrx#how-it-works", <HowItWorksContent />)} />
          <Route path="/pricing" element={msxOr("/myschoolrx/pilot", <PricingContent />)} />
          <Route path="/industries" element={msxOr("/myschoolrx", <IndustriesContent />)} />
          <Route path="/industries/community-economic-development" element={<CEDIndustryPage />} />
          <Route path="/industries/:industryKey" element={<IndustryPage />} />
          <Route path="/about" element={<MySchoolRxAbout />} />
          <Route path="/contact" element={<MySchoolRxContact />} />
          <Route path="/privacy" element={<MySchoolRxPrivacy />} />
          <Route path="/terms" element={<MySchoolRxTerms />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/admin/careers/applicants" element={<Navigate to="/app/myschoolrx/careers/applicants" replace />} />
          <Route path="/admin/careers/applicants/:aid" element={<CareersAdminRedirect />} />
          <Route path="/faq" element={msxOr("/myschoolrx#faqs", <FAQContent />)} />
          <Route path="/faq/:slug" element={msxOr("/myschoolrx#faqs", <FAQArticlePage />)} />

          {/* App shell */}
        <Route path="/demo-launch" element={<DemoLaunchHandler />} />
        <Route path="/app/*" element={<AppArea />} />

          {/* Tenant Portal (standalone, own auth) */}
          <Route path="/portal/login" element={<PortalArea />} />
          <Route path="/portal/:tenantId/payment/assessment-confirmed" element={<PortalArea />} />
          <Route path="/portal/:tenantId/payment/scope-confirmed" element={<PortalArea />} />
          <Route path="/portal/:tenantId" element={<PortalArea />} />
          <Route path="/portal/:tenantId/*" element={<PortalArea />} />

          {/* NJEA Oversight Portal (standalone, own auth) */}
          <Route path="/njea/*" element={<NJEAPortal />} />

          {/* MySchoolRx — Phase 1+2 (DB-driven landing + signup + workspace) */}
          <Route path="/myschoolrx" element={<CaporilliLanding />} />
          {/* Phase 4: dedicated School page. Portfolio page (Phase 5) still redirects interim. */}
          <Route path="/myschoolrx/schools" element={<MySchoolRxSchools />} />
          {/* Phase 5: dedicated Portfolio page. */}
          <Route path="/myschoolrx/portfolios" element={<MySchoolRxPortfolios />} />
          <Route path="/scenario-tour/:token" element={<ScenarioTourPage />} />
          <Route path="/myschoolrx/get-started" element={<SignupRouter />} />
          <Route path="/myschoolrx/pilot" element={<PilotEntry />} />
          <Route path="/myschoolrx/pilot/school" element={<PilotSingleSchool />} />
          <Route path="/myschoolrx/pilot/portfolio" element={<PilotPortfolio />} />
          <Route path="/myschoolrx/signup" element={<SchoolSignupWizard />} />
          <Route path="/diagnostic/signup" element={<SchoolSignupWizard />} />
          <Route path="/myschoolrx/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/myschoolrx/checkout/:quoteId" element={<SchoolCheckoutPage />} />
          <Route path="/ap/:token" element={<APPurchaseOrderPage />} />
          <Route path="/school-invite/:token" element={<SchoolWorkspaceSetup />} />
          <Route path="/signup/portfolio" element={<PortfolioSignup />} />
          <Route path="/myschoolrx/sample-report" element={<SampleBoardReport />} />
          <Route path="/myschoolrx/experts" element={<ExpertsPage />} />
          <Route path="/myschoolrx/guide" element={<UserGuidePage />} />
          <Route path="/diagnostic/login" element={<DiagnosticLogin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/diagnostic" element={<DiagnosticDashboard />} />
          <Route path="/diagnostic/onboarding" element={<DiagnosticOnboarding />} />
          <Route path="/diagnostic/form" element={<DiagnosticForm />} />
          <Route path="/diagnostic/:periodId/complete" element={<DiagnosticCompletion />} />
          <Route path="/diagnostic/settings" element={<TenantSettingsPage />} />
          <Route path="/diagnostic/users" element={<TenantUsersPage />} />
          <Route path="/diagnostic/upgrade/ai-plan" element={<AIPlanUpgradePage />} />

          {/* Legacy /caporilli-diagnostic/* -> /myschoolrx/* (preserves query + hash) */}
          <Route path="/caporilli-diagnostic" element={<LegacyDiagnosticRedirect />} />
          <Route path="/caporilli-diagnostic/*" element={<LegacyDiagnosticRedirect />} />
          <Route path="/school-quality-diagnostic" element={<LegacyDiagnosticRedirect />} />
          <Route path="/school-quality-diagnostic/*" element={<LegacyDiagnosticRedirect />} />

          <Route path="/portfolio/invite/:token" element={<InviteAcceptPage />} />
          <Route path="/portfolio/*" element={<PortfolioOwnerArea />} />

          <Route path="/employee/setup/:token" element={<EmployeeSetup />} />
          <Route path="/change-password" element={<ChangePasswordScreen />} />
          <Route path="/l/:token" element={<LeadLink />} />
          <Route path="/sales/*" element={<SalesPortalArea />} />
          <Route path="/test-lab/impersonate" element={<ImpersonateHandler />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>

      <Toaster richColors />
    </div>
  );
}

export default App;
